import { DataImportStatus } from '@risksmart-app/domain/src/types/consts/data-import-status';
import type { DataImportByIdResponseRow } from '@risksmart-app/drizzle/src/types';
import type { GetDataImportStatusSubscription } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetDataImportStatusDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createSubscriptionHook } from 'src/utils';

const POLL_INTERVAL_MS = 5000;

/**
 * Statuses that will not change without a user action taken on this page.
 *
 * - `complete` / `failed` are terminal.
 * - `valid` waits for the "Start import" click, and both data-import mutations
 *   invalidate `dataImportById` on success, so the query refetches and the
 *   interval below re-evaluates — no poll needed to notice the transition.
 *
 * Every other status (`notstarted`, `validating`, `initiatingImport`,
 * `importing`) is advanced by the async data-import consumer, so it must be
 * polled. `notstarted` is included deliberately: there is a window between the
 * validate mutation resolving and the consumer writing `validating`, and a
 * stopped poll would strand the page on `notstarted`.
 */
const RESTING_STATUSES: string[] = [
  DataImportStatus.Valid,
  DataImportStatus.Complete,
  DataImportStatus.Failed,
];

/**
 * Holding-pattern subscription hook for a data import's `Status` (COI-581) —
 * backs the import results tab's progress badge.
 *
 * Reuses the existing `frontend.dataImport.dataImportById` procedure (the
 * subscription selects only `Status`, a subset of that row).
 *
 * - `trpc` ON  → polls `frontend.dataImport.dataImportById` while the import is
 *   mid-pipeline, and stops once the status can only change by a user action
 *   (see `RESTING_STATUSES`). This is the one feed here whose interval is
 *   data-driven: an import is a bounded async pipeline, not an open-ended feed.
 * - `trpc` OFF → the legacy Hasura `getDataImportStatus` subscription.
 */
export const useGetDataImportStatusSubscription = createSubscriptionHook<
  { id: string },
  DataImportByIdResponseRow[],
  GetDataImportStatusSubscription
>({
  trpcQueryOptions: (trpc, { id }) =>
    trpc.frontend.dataImport.dataImportById.queryOptions({ id }),
  trpcRefetchInterval: (rows) => {
    const status = rows?.[0]?.Status;

    // No row yet (first load, or not visible) — keep polling; stopping here
    // would strand the page on an empty badge.
    if (!status) {
      return POLL_INTERVAL_MS;
    }

    return RESTING_STATUSES.includes(status) ? false : POLL_INTERVAL_MS;
  },
  mapTrpcDataToGraphQL: (rows) => ({
    data_import: rows,
  }),
  graphqlDocument: GetDataImportStatusDocument,
  graphqlVariables: ({ id }) => ({ id }),
});
